import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Eye, CalendarDays, Layers, Newspaper, Radio, LayoutTemplate, Megaphone, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

const fmt = (n, lang) => new Intl.NumberFormat(lang === "ar" ? "ar" : "en", { notation: n >= 10000 ? "compact" : "standard" }).format(n || 0);

export function AudienceChart({ daily, height = 220 }) {
  return (
    <div dir="ltr" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={daily} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
          <defs>
            <linearGradient id="vis" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7c3aed" stopOpacity={0.35} /><stop offset="100%" stopColor="#7c3aed" stopOpacity={0} /></linearGradient>
          </defs>
          <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={4} />
          <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
          <Area type="monotone" dataKey="page_views" name="Page views" stroke="#c4b5fd" fill="transparent" strokeWidth={1.5} />
          <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#7c3aed" fill="url(#vis)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200/80 rounded-xl p-5">
    <Icon size={18} className="text-violet-600" />
    <div className="mt-3 font-display font-extrabold text-3xl tracking-tight text-slate-900">{value}</div>
    <div className="mt-1 text-xs text-muted-foreground">{label}</div>
  </div>
);

export default function Advertise() {
  const { t, lang } = useI18n();
  const { data: aud, isError } = useQuery({ queryKey: ["audience"], queryFn: async () => (await api.get("/audience")).data, retry: false });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/stats")).data });

  return (
    <div className="pt-16">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-violet-700">{t("advertise.kicker")}</span>
        <h1 data-testid="advertise-title" className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight max-w-3xl">{t("advertise.title")}</h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">{t("advertise.desc")}</p>
      </section>

      {!isError && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="aud-unique-30d" icon={Users} value={fmt(aud?.unique_visitors_30d, lang)} label={t("advertise.unique30")} />
            <Stat testid="aud-pv-30d" icon={Eye} value={fmt(aud?.page_views_30d, lang)} label={t("advertise.pv30")} />
            <Stat testid="aud-avg-daily" icon={CalendarDays} value={fmt(aud?.avg_daily_visitors, lang)} label={t("advertise.avgDaily")} />
            <Stat testid="aud-ppv" icon={Layers} value={aud?.pages_per_visit ?? "—"} label={t("advertise.ppv")} />
          </div>
          <div className="mt-4 bg-white border border-slate-200/80 rounded-xl p-5">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h2 className="font-display font-bold text-base md:text-lg">{t("advertise.trend")}</h2>
              <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{t("advertise.today")}: {fmt(aud?.visitors_today, lang)}</span>
            </div>
            {aud && <AudienceChart daily={aud.daily} />}
            <p className="mt-2 text-[11px] text-muted-foreground">{t("advertise.method")}{aud?.tracking_since ? ` ${t("advertise.since")} ${aud.tracking_since}.` : ""}</p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <Stat testid="aud-items" icon={Newspaper} value={fmt(stats?.total_articles, lang)} label={t("advertise.items")} />
            <Stat testid="aud-sources" icon={Radio} value={fmt(stats?.active_sources, lang)} label={t("advertise.sources")} />
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="font-display font-bold text-base md:text-lg mb-4">{t("advertise.formats")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[[LayoutTemplate, "banner"], [Megaphone, "sponsored"]].map(([Icon, k]) => (
            <div key={k} data-testid={`ad-format-${k}`} className="bg-white border border-slate-200/80 rounded-xl p-6">
              <Icon size={20} className="text-violet-600" />
              <h3 className="mt-3 font-display font-bold text-slate-900">{t(`advertise.${k}.title`)}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(`advertise.${k}.desc`)}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl bg-slate-900 text-white p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-xl">{t("advertise.ctaTitle")}</h2>
            <p className="mt-1 text-sm text-slate-300">{t("advertise.ctaDesc")}</p>
          </div>
          <a data-testid="advertise-contact" href="mailto:info@trustedtechno.com?subject=NxtTechNews%20Advertising" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-5 py-2.5 rounded-full hover:bg-violet-100 transition-colors">
            <Mail size={16} /> info@trustedtechno.com
          </a>
        </div>
      </section>
    </div>
  );
}
